import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, ScissorsSquare, Download, RotateCcw, ShieldCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDrop } from "@/components/crop/FileDrop";
import pdfjs from "@/lib/crop/pdfjsSetup";
import { processFiles, PdfInputError } from "@/lib/crop/engine";

const MARKET_LABELS = { amazon: "Amazon", flipkart: "Flipkart", meesho: "Meesho" };
const BADGE_CLASS = {
  amazon: "bg-amber-100 text-amber-900 border-amber-300",
  flipkart: "bg-blue-100 text-blue-900 border-blue-300",
  meesho: "bg-pink-100 text-pink-900 border-pink-300",
};

function detectMarket(text) {
  const t = text.toLowerCase();
  if (t.includes("meesho") || t.includes("check the payable amount") || (t.includes("destination code") && t.includes("return code"))) return "meesho";
  if (t.includes("shopsy") || t.includes("flipkart")) return "flipkart";
  if (t.includes("amazon")) return "amazon";
  if (/e-?kart|awb no|ordered through|fssai/.test(t)) return "flipkart";
  return null;
}

async function analyzeForUi(bytes) {
  try {
    const doc = await pdfjs.getDocument({ data: bytes.slice(), disableAutoFetch: true }).promise;
    let text = "";
    const max = Math.min(doc.numPages, 2);
    for (let i = 1; i <= max; i++) {
      const page = await doc.getPage(i);
      const c = await page.getTextContent();
      text += " " + c.items.map((it) => it.str).join(" ");
    }
    return { pageCount: doc.numPages, detected: detectMarket(text) };
  } catch {
    return { pageCount: null, detected: null };
  }
}

function download(bytes, filename) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const PAGE_SIZE_OPTS = [
  { value: "default", label: "Default" },
  { value: "thermal-4x6", label: "Thermal 4 × 6 in" },
  { value: "thermal-100x150", label: "Thermal 100 × 150 mm" },
  { value: "thermal-3x5", label: "Thermal 3 × 5 in" },
  { value: "thermal-100x100", label: "Thermal 100 × 100 mm" },
  { value: "custom", label: "Custom (mm)" },
];

export function CropWidget({ mode, market, primaryLabel }) {
  const isInvoice = mode === "invoice";
  const isA4 = mode === "a4";
  const isMerge = mode === "merge";

  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [opts, setOpts] = useState({
    pageSizeChoice: "default",
    customW: 100,
    customH: 150,
    layout: isA4 ? "4up" : "default",
    rotate: true,
    includeInvoice: false,
    invoiceOnSamePage: false,
    numberOrders: false,
    orderNumberStart: 1,
    orderNumberPrefix: "",
    groupBy: isMerge ? "courier-payment" : "none",
    sortBySku: false,
    sortVariant: "none",
    summary: "false",
    summaryOrderList: true,
    customText: "",
    printDateTime: false,
  });

  const set = (k, v) => setOpts((o) => ({ ...o, [k]: v }));

  const thermalOn = opts.pageSizeChoice !== "default" && !isA4 && !isInvoice;
  const fourUp = opts.layout === "4up";
  const showVariantSort = market === "meesho" || isMerge || isInvoice;

  const addFiles = async (fileList) => {
    const pdfs = fileList.filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (pdfs.length !== fileList.length) toast.error("Only PDF files are supported.");
    const added = [];
    for (const f of pdfs) {
      if (f.size > 400 * 1024 * 1024) { toast.error(`${f.name} is too large.`); continue; }
      const buf = new Uint8Array(await f.arrayBuffer());
      const meta = await analyzeForUi(buf);
      added.push({ id: `${f.name}-${Date.now()}-${Math.random()}`, name: f.name, size: f.size, bytes: buf, ...meta });
    }
    setFiles((prev) => [...prev, ...added]);
    setResult(null);
    setError(null);
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const buildOpts = () => {
    let pageSize;
    if (thermalOn) pageSize = opts.pageSizeChoice === "custom" ? { widthMm: Number(opts.customW), heightMm: Number(opts.customH) } : opts.pageSizeChoice;
    return {
      pageSize,
      layout: isInvoice ? "default" : opts.layout,
      rotate: opts.rotate,
      includeInvoice: !isInvoice && opts.includeInvoice,
      invoiceOnSamePage: !isInvoice && opts.invoiceOnSamePage,
      numberOrders: opts.numberOrders,
      orderNumberStart: opts.orderNumberStart,
      orderNumberPrefix: opts.orderNumberPrefix,
      groupBy: opts.groupBy,
      sortBySku: opts.sortBySku,
      sortVariant: opts.sortVariant,
      invoicesOnly: isInvoice,
      summary: opts.summary === "false" ? false : opts.summary,
      summaryOrderList: opts.summaryOrderList,
      customText: opts.customText,
      printDateTime: opts.printDateTime,
      __forceMarket: market || undefined,
    };
  };

  const run = async () => {
    if (!files.length) { toast.error("Please add at least one PDF first."); return; }
    setProcessing(true);
    setError(null);
    setResult(null);
    try {
      const payload = files.map((f) => ({ name: f.name, bytes: f.bytes, market: market || undefined }));
      const res = await processFiles(payload, buildOpts(), pdfjs);
      const fname = `ecroply-${mode === "marketplace" ? market : mode}-labels.pdf`;
      download(res.bytes, fname);
      setResult({ ...res, fname });
      toast.success("Done! Your PDF has downloaded.");
    } catch (e) {
      const msg = e instanceof PdfInputError ? e.message : "Something went wrong while processing. Please check your file and try again.";
      setError(msg);
      toast.error("Could not process the file.");
    } finally {
      setProcessing(false);
    }
  };

  const reset = () => { setFiles([]); setResult(null); setError(null); };

  const Row = ({ title, desc, children, disabled }) => (
    <div className={`flex items-start justify-between gap-4 py-3 ${disabled ? "opacity-50" : ""}`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-800">{title}</p>
        {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );

  const marketBadge = (m) => m && (
    <Badge variant="outline" className={`${BADGE_CLASS[m]} font-semibold`}>{MARKET_LABELS[m]}</Badge>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-7 shadow-xl shadow-slate-200/50" data-testid="crop-widget">
      {!result ? (
        <>
          <FileDrop
            files={files}
            onAdd={addFiles}
            onRemove={removeFile}
            multiple
            hint={isMerge ? "Add Amazon, Flipkart & Meesho PDFs together" : market ? `${MARKET_LABELS[market] || "Marketplace"} label PDFs` : "Marketplace label PDFs"}
            renderMeta={(isMerge || isA4 || isInvoice) ? (f) => (
              <div className="mt-1">
                {f.detected ? marketBadge(f.detected) : <span className="text-xs text-amber-600 font-medium inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Marketplace not detected</span>}
              </div>
            ) : undefined}
          />

          {/* Options */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5 divide-y divide-slate-200" data-testid="options-panel">
            {!isInvoice && !isA4 && (
              <Row title="Label size" desc="Choose the size that matches the label roll in your printer, and print at 100% / actual size." disabled={fourUp || opts.invoiceOnSamePage}>
                <Select value={opts.pageSizeChoice} onValueChange={(v) => set("pageSizeChoice", v)} disabled={fourUp || opts.invoiceOnSamePage}>
                  <SelectTrigger className="w-48" data-testid="select-page-size"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Row>
            )}

            {thermalOn && opts.pageSizeChoice === "custom" && (
              <div className="flex items-center gap-3 py-3">
                <div>
                  <Label className="text-xs">Width (mm)</Label>
                  <Input type="number" min={30} max={300} value={opts.customW} onChange={(e) => set("customW", e.target.value)} className="w-24" data-testid="input-custom-width" />
                </div>
                <div>
                  <Label className="text-xs">Height (mm)</Label>
                  <Input type="number" min={30} max={300} value={opts.customH} onChange={(e) => set("customH", e.target.value)} className="w-24" data-testid="input-custom-height" />
                </div>
              </div>
            )}

            {!isInvoice && !isA4 && (
              <Row title="Labels per page" disabled={thermalOn || opts.invoiceOnSamePage}>
                <Select value={opts.layout} onValueChange={(v) => set("layout", v)} disabled={thermalOn || opts.invoiceOnSamePage}>
                  <SelectTrigger className="w-40" data-testid="select-layout"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">1 per page</SelectItem>
                    <SelectItem value="4up">4 per A4 (2×2)</SelectItem>
                  </SelectContent>
                </Select>
              </Row>
            )}

            {(thermalOn || fourUp) && (
              <Row title="Rotate wide labels to fill the sheet">
                <Switch checked={opts.rotate} onCheckedChange={(v) => set("rotate", v)} data-testid="switch-rotate" />
              </Row>
            )}

            {!isInvoice && (
              <>
                <Row title="Include invoice" desc="Invoice follows its label as an A4 page.">
                  <Switch checked={opts.includeInvoice} onCheckedChange={(v) => set("includeInvoice", v)} data-testid="switch-include-invoice" />
                </Row>
                <Row title="Label and invoice on the same page" desc="One A4 sheet per order with a dashed divider." disabled={thermalOn || fourUp}>
                  <Switch checked={opts.invoiceOnSamePage} disabled={thermalOn || fourUp} onCheckedChange={(v) => set("invoiceOnSamePage", v)} data-testid="switch-invoice-same-page" />
                </Row>
              </>
            )}

            <Row title="Number the orders">
              <Switch checked={opts.numberOrders} onCheckedChange={(v) => set("numberOrders", v)} data-testid="switch-number-orders" />
            </Row>
            {opts.numberOrders && (
              <div className="flex items-center gap-3 py-3">
                <div>
                  <Label className="text-xs">Start at</Label>
                  <Input type="number" min={0} max={999999} value={opts.orderNumberStart} onChange={(e) => set("orderNumberStart", e.target.value)} className="w-28" data-testid="input-number-start" />
                </div>
                <div>
                  <Label className="text-xs">Prefix</Label>
                  <Input maxLength={10} value={opts.orderNumberPrefix} onChange={(e) => set("orderNumberPrefix", e.target.value)} placeholder="#" className="w-28" data-testid="input-number-prefix" />
                </div>
              </div>
            )}

            <Row title="Group by" desc={market === "amazon" ? "Amazon labels are images, so courier reads as Amazon Transport and payment as Unknown." : undefined}>
              <Select value={opts.groupBy} onValueChange={(v) => set("groupBy", v)}>
                <SelectTrigger className="w-48" data-testid="select-group-by"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="marketplace">Marketplace</SelectItem>
                  <SelectItem value="courier">Courier</SelectItem>
                  <SelectItem value="payment">Payment</SelectItem>
                  <SelectItem value="courier-payment">Courier &amp; payment</SelectItem>
                </SelectContent>
              </Select>
            </Row>

            <Row title="Sort by SKU">
              <Switch checked={opts.sortBySku} onCheckedChange={(v) => set("sortBySku", v)} data-testid="switch-sort-sku" />
            </Row>

            {showVariantSort && (
              <Row title="Sort by colour and size" desc="Works for Meesho labels only; other marketplaces sort last.">
                <Select value={opts.sortVariant} onValueChange={(v) => set("sortVariant", v)}>
                  <SelectTrigger className="w-44" data-testid="select-sort-variant"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="color-size">Colour, then size</SelectItem>
                    <SelectItem value="size-color">Size, then colour</SelectItem>
                  </SelectContent>
                </Select>
              </Row>
            )}

            <Row title="Summary (SKU + order list)" desc="A picking list with total units per SKU and parcels per courier.">
              <Select value={opts.summary} onValueChange={(v) => set("summary", v)}>
                <SelectTrigger className="w-40" data-testid="select-summary"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">Off</SelectItem>
                  <SelectItem value="separate">Separate file</SelectItem>
                  <SelectItem value="first">First page</SelectItem>
                  <SelectItem value="last">Last page</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            {opts.summary !== "false" && (
              <Row title="Include the order list" desc="Order number, SKU, qty, courier, payment.">
                <Switch checked={opts.summaryOrderList} onCheckedChange={(v) => set("summaryOrderList", v)} data-testid="switch-order-list" />
              </Row>
            )}

            <div className="py-3">
              <Label className="text-sm font-semibold text-slate-800">Custom text (dispatch note / thank-you)</Label>
              <Input
                maxLength={60}
                value={opts.customText}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/[^\u0000-\u00FF]/.test(v)) toast.warning("Characters outside Latin-1 will print as “?”.");
                  set("customText", v);
                }}
                placeholder="Thanks for shopping with us!"
                className="mt-1.5"
                data-testid="input-custom-text"
              />
            </div>

            <Row title="Print date & time">
              <Switch checked={opts.printDateTime} onCheckedChange={(v) => set("printDateTime", v)} data-testid="switch-print-date" />
            </Row>
          </div>

          {error && (
            <div className="mt-5 flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-xl p-4" data-testid="error-state">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          <Button onClick={run} disabled={processing || !files.length} className="w-full mt-6 h-12 text-base bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/25" data-testid="process-button">
            {processing ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Processing…</> : <><ScissorsSquare className="w-5 h-5 mr-2" /> {primaryLabel}</>}
          </Button>
          <p className="text-xs text-slate-500 text-center mt-3 inline-flex items-center justify-center gap-1.5 w-full">
            <ShieldCheck className="w-3.5 h-3.5 text-blue-500" />
            Your PDF is processed in your browser on this device and is not uploaded to a server.
          </p>
        </>
      ) : (
        <div data-testid="result-state" className="animate-float-up">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Download className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg text-slate-900">Your PDF is ready</h3>
              <p className="text-sm text-slate-500">{result.labels} label{result.labels === 1 ? "" : "s"} · {result.pages} order{result.pages === 1 ? "" : "s"} processed</p>
            </div>
          </div>

          {result.skipped > 0 && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="skipped-note">
              {result.skipped} order{result.skipped === 1 ? "" : "s"} had no invoice in the PDF and {result.skipped === 1 ? "was" : "were"} skipped.
            </p>
          )}

          {result.summary?.couriers?.length > 0 && (
            <div className="mb-5 rounded-2xl border border-slate-200 overflow-hidden" data-testid="courier-table">
              <div className="bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">By courier &amp; payment</div>
              <table className="w-full text-sm">
                <tbody>
                  {result.summary.couriers.map((c, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-4 py-2 text-slate-700">{c.courier}</td>
                      <td className="px-4 py-2 text-slate-500">{c.payment}</td>
                      <td className="px-4 py-2 text-right font-semibold text-slate-900">{c.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => download(result.bytes, result.fname)} className="flex-1 h-11 bg-blue-600 hover:bg-blue-700" data-testid="download-button">
              <Download className="w-4 h-4 mr-2" /> Download again
            </Button>
            {result.summaryBytes && (
              <Button variant="outline" onClick={() => download(result.summaryBytes, "ecroply-summary.pdf")} className="flex-1 h-11" data-testid="download-summary-button">
                <Download className="w-4 h-4 mr-2" /> Download summary
              </Button>
            )}
            <Button variant="ghost" onClick={reset} className="h-11" data-testid="process-another-button">
              <RotateCcw className="w-4 h-4 mr-2" /> Process another file
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
